import { PracticeQueryParams } from 'constants/application-constants'

import { useEffect } from 'react'

import {
  Card,
  LoadingIndicator,
  PageTitle,
  Section,
  SectionHeader,
} from 'components/common'
import { DocumentsTable } from 'components/features/Documents/components'
import { permissionsActions } from 'components/features/Other/api/permissionsSlice'
import {
  useAppDispatch,
  useAppSelector,
  usePermissions,
  useToggle,
} from 'hooks'
import { t } from 'i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import EmptyDocumentsList from './EmptyDocumentsList'
import MainDocumentCard from './MainDocumentCard'
import MetadataDetailCard from './MetadataDetailCard'
import styles from './PracticeInformation.module.scss'
import RevisionCycleCard from './RevisionCycleCard'
import { RevisionStatus } from '../../../../../redux-store/models'
import {
  currentDocumentsSelector,
  removeMainDocument,
  setDocuments,
  setMainDocument,
} from '../../../Documents/api/currentDocumentsSlice'
import {
  currentMaterialsSelector,
  setMaterials,
} from '../../../Materials/api/currentMaterialSlice'
import { materialsApi } from '../../../Materials/api/materialsApi'
import MaterialsTable from '../../../Materials/components/MaterialsTable/MaterialsTable'
import {
  practicesApi,
  selectCurrentRevision,
  selectIsCurrentRevisionLoading,
} from '../../api'
import { selectPractice } from '../../api/practiceSlice'
import PracticeSidebar from '../PracticeSidebar/PracticeSidebar'

const PracticeInformation = () => {
  const dispatch = useAppDispatch()
  const [isModalOpen, toggleSetOutdated] = useToggle(false)
  const currentRevision = useAppSelector(selectCurrentRevision)
  const currentRevisionLoading = useAppSelector(selectIsCurrentRevisionLoading)
  const currentMainDocument = useAppSelector(
    currentDocumentsSelector.selectMainDocument
  )
  const currentDocuments = useAppSelector(
    currentDocumentsSelector.selectCurrentDocuments
  )
  const currentMaterials = useAppSelector(
    currentMaterialsSelector.selectCurrentMaterials
  )

  const practice = useAppSelector(selectPractice)
  const [searchParams] = useSearchParams()
  const { id = '' } = useParams()
  const [httpGetMaterials] = materialsApi.useLazyGetMaterialsQuery()

  // TODO: move active language information to store
  const activeLanguageCode =
    searchParams.get(PracticeQueryParams.METADATA_LANGUAGE) ||
    currentRevision?.metadata[0].language.code

  const currentMetadata = currentRevision?.metadata.find(
    ({ language }) => activeLanguageCode === language.code
  )

  const httpGetExistingPractices =
    practicesApi.useGetExistingPracticesQuery(null)

  const httpGetPracticeState =
    practicesApi.endpoints.getPracticeById.useQueryState(id)

  usePermissions(id) // Ensure permissions for guideline get fetched

  useEffect(() => {
    dispatch(permissionsActions.setEnforcedGuideline(!!id ? id : null))

    return () => {
      dispatch(permissionsActions.setEnforcedGuideline(null))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (
      !httpGetPracticeState.isFetching &&
      !httpGetPracticeState.isError &&
      httpGetPracticeState.isSuccess
    ) {
      if (
        !!httpGetPracticeState.data?.data?.current_draftish_revision
          ?.main_document &&
        httpGetPracticeState.data.data.current_draftish_revision?.main_document
          .length > 0
      ) {
        dispatch(
          setMainDocument(
            httpGetPracticeState.data.data.current_draftish_revision
              .main_document[0]
          )
        )
      } else {
        dispatch(removeMainDocument())
      }

      // set documents
      if (
        !!httpGetPracticeState.data?.data?.current_draftish_revision
          ?.documents &&
        httpGetPracticeState.data.data.current_draftish_revision?.documents
          .length > 0
      ) {
        dispatch(
          setDocuments(
            httpGetPracticeState.data.data.current_draftish_revision.documents
          )
        )
      } else {
        dispatch(setDocuments([]))
      }
    }
  }, [
    httpGetPracticeState.isFetching,
    httpGetPracticeState.isError,
    httpGetPracticeState.isSuccess,
    httpGetPracticeState.data,
    dispatch,
  ])

  useEffect(() => {
    const fetchMaterials = async (_practiceId: string) => {
      try {
        const { data } = await httpGetMaterials({
          practiceId: _practiceId,
        }).unwrap()
        dispatch(setMaterials(data))
      } catch (e) {
        dispatch(setMaterials([]))
      }
    }

    if (
      !httpGetPracticeState.isFetching &&
      httpGetPracticeState.isSuccess &&
      httpGetPracticeState.data?.data?.has_materials === true
    ) {
      fetchMaterials(id)
    } else {
      dispatch(setMaterials([]))
    }
  }, [
    httpGetMaterials,
    httpGetPracticeState.isSuccess,
    httpGetPracticeState.isFetching,
    httpGetPracticeState.data,
    dispatch,
    id,
  ])

  const isLoading = httpGetPracticeState.isFetching || currentRevisionLoading
  const isError = !isLoading && httpGetPracticeState.isError

  if (httpGetPracticeState.isFetching || currentRevisionLoading) {
    return (
      <div className={styles.loader}>
        <LoadingIndicator />
      </div>
    )
  }

  // ERROR/ EMPTY STATE
  if (!isError && !currentMetadata) {
    return null
  }

  if (isError) {
    // @ts-ignore
    const practiceErrorStatus = httpGetPracticeState?.error?.status

    return (
      <div className={styles.root}>
        <div className={styles.body}>
          <Section>
            <PageTitle title={t('practice.loadingErrorTitle')} />
            {practiceErrorStatus === 404 && (
              <div>{t('practice.loadingError404')}</div>
            )}
            {practiceErrorStatus !== 404 && (
              <div>{t('practice.loadingError')}</div>
            )}
          </Section>
        </div>
      </div>
    )
  }

  const data = httpGetPracticeState.data?.data

  var mainDocMetadata = null
  var status = currentRevision?.status

  if (currentMainDocument) {
    // Access proper Metadata value with fallbacks
    let temp
    const mainDocument = currentMainDocument

    // depending on the revision status, choose the correct metadata to display
    // todo: extract this into a method
    let documentMetadataAccessor:
      | 'latest_approved_version'
      | 'current_approvalish_version'
      | 'latest_draft_version'

    switch (currentRevision?.status) {
      case RevisionStatus.draft:
      case RevisionStatus.precheck:
        documentMetadataAccessor = 'latest_draft_version'
        break

      case RevisionStatus.approval:
      case RevisionStatus.preapproval:
        documentMetadataAccessor = 'current_approvalish_version'
        break

      case RevisionStatus.published:
        documentMetadataAccessor = 'latest_approved_version'
        break

      default:
        documentMetadataAccessor = 'latest_approved_version'
    }

    temp = mainDocument && mainDocument[documentMetadataAccessor]?.metadata
    mainDocMetadata = temp && 0 in temp ? temp[0] : mainDocument.metadata
  }

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        {currentMetadata && (
          <Section>
            <PageTitle
              title={currentMetadata.title}
              description={currentMetadata.subject}
            />
            <div className={styles.wrapper}>
              {practice && data && httpGetExistingPractices.isSuccess && (
                <MetadataDetailCard
                  metadata={currentMetadata}
                  author={data.author}
                  practice={practice}
                  practices={httpGetExistingPractices.data.data}
                  isModalOpen={isModalOpen}
                  toggleModal={toggleSetOutdated}
                />
              )}
              {currentRevision && (
                <RevisionCycleCard
                  practice={practice}
                  currentRevision={currentRevision}
                  toggleModal={toggleSetOutdated}
                />
              )}
            </div>
          </Section>
        )}

        {currentRevisionLoading && <LoadingIndicator />}

        {!currentRevisionLoading && (
          <>
            {mainDocMetadata && currentMainDocument?.id && (
              <Section>
                <SectionHeader title={t('practice.mainDocument')} />

                <MainDocumentCard
                  document={mainDocMetadata}
                  documentFileExtension={
                    currentMainDocument?.latest_draft_version?.files[0]
                      ?.extension
                  }
                  docId={currentMainDocument.id}
                  guidelineId={id}
                />
              </Section>
            )}

            {currentDocuments && currentDocuments.length > 0 && (
              <Section>
                <SectionHeader title={t('practice.attachments')} />
                <Card hasTable>
                  <Card.Body className={styles.docBody}>
                    <DocumentsTable
                      documents={currentDocuments}
                      status={status}
                      guidelineId={id}
                    />
                  </Card.Body>
                </Card>
              </Section>
            )}

            {(!currentDocuments || currentDocuments.length === 0) &&
              !mainDocMetadata && <EmptyDocumentsList />}

            {currentMaterials && currentMaterials.length > 0 && (
              <Section>
                <SectionHeader title={t('practice.materials')} />
                <Card hasTable>
                  <Card.Body className={styles.docBody}>
                    <MaterialsTable
                      materials={currentMaterials}
                      guidelineId={id}
                    ></MaterialsTable>
                  </Card.Body>
                </Card>
              </Section>
            )}
          </>
        )}
      </div>
      <PracticeSidebar />
    </div>
  )
}

export default PracticeInformation
